// Deal Model - matches Flutter implementation

export interface PageLayout {
  fit: 'cover' | 'contain';
  posX: number; // 0-100 → CSS object-position X %
  posY: number; // 0-100 → CSS object-position Y %
  zoom: number; // 100-300 → scale %
}

export interface ImageLayout {
  auth?: PageLayout;
  welcome?: PageLayout;
}

export interface LogoLayout {
  scale: number; // percent, default 100 (range 50-200)
  align: 'left' | 'center' | 'right';
}

export interface Callout {
  icon?: string; // storage ref (small badge image)
  title?: string;
  body?: string;
  pages?: string[]; // promo pages it shows on: welcome|login|signup|verify|redeem|success
}

export interface DealTranslation {
  message?: string;
  headline?: string;
  calloutTitle?: string;
  calloutBody?: string;
  ctaText?: string;
}

// Per-locale overrides for the prominent deal copy. Absent locale → default fields.
export interface DealTranslations {
  fr?: DealTranslation;
  [locale: string]: DealTranslation | undefined;
}

export interface Deal {
  id?: string;
  name: string;
  message?: string;
  logo?: string; // logoUrl in Dart
  images?: string[];
  authImage?: string; // Login + Signup (+ Verify, Redeem form)
  welcomeImage?: string; // Welcome + Success screens
  imageLayout?: ImageLayout;
  logoLayout?: LogoLayout;
  callout?: Callout;
  translations?: DealTranslations;
  defaultLanguage?: string; // 'en' | 'fr' — opens the deal in this language unless the user has chosen one
  headline?: string; // Optional headline override (Welcome + Signup)
  ctaText?: string; // Optional primary CTA label override (Welcome + Signup)
  description?: string;
  backgroundColor?: string;
  textColor?: string;
  isRedemption?: boolean;
}

/**
 * Parse hex color string to CSS color
 */
export const fromHex = (hexString: string): string => {
  if (!hexString) return '';

  // Remove # if present and ensure it's a valid hex
  const cleanHex = hexString.replace('#', '');

  // Add opacity if only 6 characters (RGB)
  if (cleanHex.length === 6) {
    return `#${cleanHex}`;
  } else if (cleanHex.length === 8) {
    // ARGB format - convert to RGBA
    const alpha = cleanHex.substring(0, 2);
    const rgb = cleanHex.substring(2);
    return `#${rgb}${alpha}`;
  }

  return `#${cleanHex}`;
};

/**
 * Create Deal from Firestore document
 */
export const dealFromMap = (map: Record<string, any>): Deal => {
  return {
    id: map.id,
    name: map.name || '',
    message: map.message,
    logo: map.logo,
    images: map.images ? Array.from(map.images) : undefined,
    authImage: map.authImage,
    welcomeImage: map.welcomeImage,
    imageLayout: map.imageLayout,
    logoLayout: map.logoLayout,
    callout: map.callout,
    translations: map.translations,
    defaultLanguage: map.defaultLanguage,
    headline: map.headline,
    ctaText: map.ctaText,
    description: map.description,
    backgroundColor: map.backgroundColor,
    textColor: map.textColor,
    isRedemption: map.isRedemption,
  };
};

/**
 * Convert Deal to map for storage
 */
export const dealToMap = (deal: Deal): Record<string, any> => {
  return {
    id: deal.id,
    name: deal.name,
    message: deal.message,
    logo: deal.logo,
    images: deal.images,
    authImage: deal.authImage,
    welcomeImage: deal.welcomeImage,
    imageLayout: deal.imageLayout,
    logoLayout: deal.logoLayout,
    callout: deal.callout,
    translations: deal.translations,
    defaultLanguage: deal.defaultLanguage,
    headline: deal.headline,
    ctaText: deal.ctaText,
    description: deal.description,
    backgroundColor: deal.backgroundColor,
    textColor: deal.textColor,
    isRedemption: deal.isRedemption,
  };
};
